import { Box3, Vector3, Web3DViewer } from '@technology/web3d';
import { PotreeManagerModel } from '@technology/web3d-plugin-potreemanager';

/**
 * @intent Finds the elevation (Z) of the loaded point-cloud point nearest to the
 * given scene X,Y, measuring distance in the X,Y plane only. Used to drop a
 * marker onto the surface when a feature has no explicit elevation.
 *
 * Only point sets whose world bounding box covers the X,Y are scanned, so the
 * cost scales with points near the query rather than the whole cloud. Just the
 * currently-loaded LOD point sets participate; unloaded/sparse areas yield
 * undefined.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 16 > 15 allowed; refactor later
export function sampleClosestPointCloudElevation(
  viewer: Web3DViewer,
  x: number,
  y: number
): number | undefined {
  const point = new Vector3();
  const worldBox = new Box3();
  let closestZ: number | undefined;
  let closestDistanceSq = Infinity;

  for (const model of viewer.getModels(PotreeManagerModel)) {
    for (const pointset of model.pointCloud.pointsets) {
      const localBox = pointset.geometry.boundingBox;
      if (!localBox) {
        continue;
      }
      worldBox.copy(localBox).applyMatrix4(pointset.matrixWorld);
      if (
        x < worldBox.min.x ||
        x > worldBox.max.x ||
        y < worldBox.min.y ||
        y > worldBox.max.y
      ) {
        continue;
      }

      const positions = pointset.geometry.getAttribute('position');
      const matrixWorld = pointset.matrixWorld;
      for (let i = 0, count = positions.count; i < count; i++) {
        point
          .set(positions.getX(i), positions.getY(i), positions.getZ(i))
          .applyMatrix4(matrixWorld);
        const dx = point.x - x;
        const dy = point.y - y;
        const distanceSq = dx * dx + dy * dy;
        if (distanceSq < closestDistanceSq) {
          closestDistanceSq = distanceSq;
          closestZ = point.z;
        }
      }
    }
  }

  return closestZ;
}
